"use client"

import { useState, Fragment, useEffect } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  X,
  DollarSign,
  ArrowRight,
  CreditCard,
  Wallet,
  RefreshCw,
  AlertCircle,
  Info,
} from "lucide-react"
import { InputCustom } from "../../components"
import adAccountsService from "../../services/adAccountsService"
import transferHistoryService from "../../services/transferHistoryService"
import { useCurrentUser } from "../../utils/context/UserProvider"
import toast from "react-hot-toast"
import { Globe, GlobeSimple } from "phosphor-react"

const WithdrawBalanceModal = ({ isOpen, onClose, account, onSuccess }) => {
  const [transferAmount, setTransferAmount] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)
  const [transferComplete, setTransferComplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})

  // ✅ fee states
  const [feePercentage, setFeePercentage] = useState(0)
  const [feeAmount, setFeeAmount] = useState(0)

  // ✅ net to credit = withdraw + fee
  const [netToCredit, setNetToCredit] = useState(0)

  // ✅ total debit from ad account (same as netToCredit in this model)
  const [totalDebitFromAd, setTotalDebitFromAd] = useState(0)

  const { currentUser } = useCurrentUser()

  useEffect(() => {
    if (isOpen) {
      setTransferAmount("")
      setIsProcessing(false)
      setTransferComplete(false)
      setErrors({})
      setFeePercentage(0)
      setFeeAmount(0)
      setNetToCredit(0)
      setTotalDebitFromAd(0)

      if (account?._id) fetchAccountData(account._id)
    }
  }, [isOpen, account])

  useEffect(() => {
    if (transferAmount && formData.platform) {
      calculateFeeAndNet()
    } else {
      setFeePercentage(0)
      setFeeAmount(0)
      setNetToCredit(Number.parseFloat(transferAmount) || 0)
      setTotalDebitFromAd(Number.parseFloat(transferAmount) || 0)
    }
  }, [transferAmount, formData.platform, formData.feePercentage])

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  const getPlatformDisplayName = (platform) => {
    switch (platform) {
      case "meta":
        return "Meta"
      case "google":
        return "Google"
      case "tiktok":
        return "TikTok"
      case "snapchat":
        return "Snapchat"
      default:
        return platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : "Unknown"
    }
  }

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "meta":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 48 48">
            <path
              fill="#0081fb"
              d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38c-0.06,0.09-0.13,0.18-0.19,0.28c-0.04,0.05-0.07,0.1-0.11,0.16c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83c-0.09,0.17-0.19,0.34-0.28,0.51c-0.03,0.04-0.06,0.09-0.08,0.13l-0.21,0.37l-1.24,2.19c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57c0.1-0.14,0.2-0.28,0.31-0.42c0.1-0.14,0.21-0.28,0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1c1.85-1.61,3.84-2.5,5.85-2.5c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5C47,29.03,47,29.19,47,29.36z"
            ></path>
            <linearGradient
              id="meta1"
              x1="42.304"
              x2="13.533"
              y1="24.75"
              y2="24.75"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#0081fb"></stop>
              <stop offset=".995" stopColor="#0064e1"></stop>
            </linearGradient>
            <path
              fill="url(#meta1)"
              d="M4.918,15.456 C7.195,11.951,10.483,9.5,14.253,9.5c2.184,0,4.354,0.645,6.621,2.493c2.479,2.02,5.122,5.346,8.419,10.828l1.182,1.967 c2.854,4.746,4.477,7.187,5.428,8.339C37.125,34.606,37.888,35,39,35c2.82,0,3.617-2.54,3.617-5.501L47,29.362 c0,3.095-0.611,5.369-1.651,7.165C44.345,38.264,42.387,40,39.093,40c-2.048,0-3.862-0.444-5.868-2.333 c-1.542-1.45-3.345-4.026-4.732-6.341l-4.126-6.879c-2.07-3.452-3.969-6.027-5.068-7.192c-1.182-1.254-2.642-2.754-5.067-2.754 c-1.963,0-3.689,1.362-5.084,3.465L4.918,15.456z"
            ></path>
          </svg>
        )
      case "google":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 48 48">
            <polygon fill="#ffc107" points="30.129,15.75 18.871,9.25 5.871,31.25 17.129,37.75"></polygon>
            <path
              fill="#1e88e5"
              d="M31.871,37.75c1.795,3.109,5.847,4.144,8.879,2.379c3.103-1.806,4.174-5.77,2.379-8.879l-13-22 c-1.795-3.109-5.835-4.144-8.879-2.379c-3.106,1.801-4.174,5.77-2.379,8.879L31.871,37.75z"
            ></path>
            <circle cx="11.5" cy="34.5" r="6.5" fill="#43a047"></circle>
          </svg>
        )
      case "tiktok":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="30" height="35" viewBox="0 0 48 48">
            <path
              fill="#212121"
              fillRule="evenodd"
              d="M10.904,6h26.191C39.804,6,42,8.196,42,10.904v26.191 C42,39.804,39.804,42,37.096,42H10.904C8.196,42,6,39.804,6,37.096V10.904C6,8.196,8.196,6,10.904,6z"
              clipRule="evenodd"
            ></path>
            <path
              fill="#ec407a"
              fillRule="evenodd"
              d="M29.208,20.607c1.576,1.126,3.507,1.788,5.592,1.788v-4.011 c-0.395,0-0.788-0.041-1.174-0.123v3.157c-2.085,0-4.015-0.663-5.592-1.788v8.184c0,4.094-3.321,7.413-7.417,7.413 c-1.528,0-2.949-0.462-4.129-1.254c1.347,1.376,3.225,2.23,5.303,2.23c4.096,0,7.417-3.319,7.417-7.413L29.208,20.607L29.208,20.607 z"
              clipRule="evenodd"
            ></path>
            <path
              fill="#fff"
              fillRule="evenodd"
              d="M28.034,19.63c1.576,1.126,3.507,1.788,5.592,1.788v-3.157 c-1.164-0.248-2.194-0.856-2.969-1.701c-1.326-0.827-2.281-2.191-2.561-3.788h-2.923v16.018c-0.007,1.867-1.523,3.379-3.393,3.379 c-1.102,0-2.081-0.525-2.701-1.338c-1.107-0.558-1.866-1.705-1.866-3.029c0-1.873,1.519-3.391,3.393-3.391 c0.359,0,0.705,0.056,1.03,0.159V21.38c-4.024,0.083-7.26,3.369-7.26,7.411c0,2.018,0.806,3.847,2.114,5.183 c1.18,0.792,2.601,1.254,4.129,1.254c4.096,0,7.417-3.319,7.417-7.413L28.034,19.63L28.034,19.63z"
              clipRule="evenodd"
            ></path>
          </svg>
        )
      case "snapchat":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 48 48">
            <path
              fill="#ffeb3b"
              d="M24,4C12.402,4,3,13.402,3,25c0,11.598,9.402,21,21,21s21-9.402,21-21C45,13.402,35.598,4,24,4z"
            ></path>
            <path
              fill="#fff"
              d="M24,36c-2.451,0-4.045-1.09-4.827-1.612c-0.477-0.317-0.924-0.616-1.317-0.705 c-0.345-0.078-0.685-0.118-1.012-0.118c-0.59,0-1.056,0.103-1.386,0.175c-0.207,0.045-0.385,0.084-0.526,0.084 c-0.143,0-0.298-0.032-0.367-0.267c-0.064-0.218-0.107-0.428-0.148-0.631c-0.116-0.568-0.198-0.92-0.416-0.954 c-2.562-0.401-3.295-0.945-3.464-1.331c-0.024-0.055-0.037-0.11-0.04-0.165c-0.008-0.138,0.09-0.258,0.227-0.283 c3.731-0.614,5.399-4.440,5.467-4.589c0.004-0.009,0.008-0.017,0.012-0.026c0.225-0.456,0.268-0.851,0.131-1.174 c-0.258-0.611-1.099-0.879-1.657-1.058c-0.137-0.044-0.268-0.086-0.371-0.126c-0.504-0.198-1.349-0.612-1.241-1.186 c0.078-0.416,0.615-0.707,1.049-0.707c0.12,0,0.226,0.021,0.316,0.063c0.518,0.242,0.986,0.364,1.392,0.364 c0.558,0,0.802-0.236,0.833-0.266c-0.015-0.273-0.033-0.557-0.051-0.846c-0.119-1.922-0.268-4.313,0.337-5.674 C18.915,13.058,22.304,13,23.104,13h0.792c0.8,0,4.189,0.058,6.254,3.347c0.605,1.359,0.456,3.75,0.337,5.673 c-0.018,0.29-0.036,0.573-0.051,0.847c0.031,0.03,0.275,0.266,0.833,0.266c0.406,0,0.874-0.122,1.392-0.364 c0.09-0.042,0.196-0.063,0.316-0.063c0.433,0,0.971,0.291,1.049,0.707c0.108,0.574-0.737,0.988-1.241,1.186 c-0.103,0.04-0.234,0.082-0.371,0.126c-0.558,0.179-1.399,0.447-1.657,1.058c-0.137,0.323-0.094,0.718,0.131,1.174 c0.004,0.009,0.008,0.017,0.012,0.026c0.068,0.149,1.736,3.975,5.467,4.589c0.137,0.025,0.235,0.145,0.227,0.283 c-0.003,0.055-0.016,0.11-0.04,0.165c-0.169,0.386-0.902,0.93-3.464,1.331c-0.218,0.034-0.224,0.267-0.367,0.267 c-0.142,0-0.319-0.039-0.526-0.084c-0.33-0.072-0.796-0.175-1.386-0.175c-0.327,0-0.667,0.04-1.012,0.118 c-0.393,0.089-0.84,0.388-1.317,0.705C28.045,34.91,26.451,36,24,36z"
            ></path>
          </svg>
        )
      default:
        return <Globe size={20} className="text-neutral-800 dark:text-neutral-400" />
    }
  }


  const fetchAccountData = async (id) => {
    try {
      setLoading(true)
      const response = await adAccountsService.get(id)
      const responseData = response.data

      if (responseData.data && responseData.statusCode === 200) {
        const d = responseData.data

        const accountData = {
          platform: d.platform || "meta",
          currency: d.currency || "USD",

          credit_account: d.credit_account?._id || "",
          credit_account_name: d.credit_account?.account_name || "",
          credit_account_balance: d.credit_account?.balance || 0,
          credit_account_currency: d.credit_account?.account_currency || "USD",

          client: d.user?._id || "",
          clientName: d.user?.reference || d.user?.fullName || "",
          clientEmail: d.user?.email || "",

          accountName: d.account_name || "",
          accountAlias: d.account_alias || "",

          // ✅ IMPORTANT: ad account balance (source for withdrawal)
          balance: d.balance || 0,

          // fee percentage from the ad account itself
          feePercentage: d.feePercentage || 0,
        }

        setFormData(accountData)
      }
    } catch (error) {
      console.error("Failed to load account data:", error)
      toast.error("Failed to load account data")
    } finally {
      setLoading(false)
    }
  }

  // ✅ FEE ADDED model: net = withdraw + fee
  const calculateFeeAndNet = () => {
    const amount = Number.parseFloat(transferAmount) || 0
    const pct = Number(formData.feePercentage || 0)

    const fee = (amount * pct) / 100
    const net = amount + fee

    setFeePercentage(pct)
    setFeeAmount(fee)
    setNetToCredit(net)
    setTotalDebitFromAd(net) // debit includes fee too
  }

  const handleAmountChange = (value) => {
    const amount = value.replace(/[^0-9.]/g, "")
    setTransferAmount(amount)

    if (errors.transferAmount) {
      setErrors((p) => ({ ...p, transferAmount: "" }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    const w = Number.parseFloat(transferAmount)
    if (!transferAmount) {
      newErrors.transferAmount = "Withdrawal amount is required"
    } else if (!Number.isFinite(w) || w <= 0) {
      newErrors.transferAmount = "Please enter a valid amount greater than 0"
    }


    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleWithdrawBalance = async () => {
    if (!validateForm()) return

    setIsProcessing(true)
    try {
      const withdrawAmount = Number.parseFloat(transferAmount) || 0

      const response = await transferHistoryService.withdrawBalance({
        sourceAccountId: account._id,              // AdAccount
        targetAccountId: formData.credit_account,  // CreditAccount

        amount: withdrawAmount,                    // base withdraw
        fee: feeAmount,                            // positive
        feePercentage: feePercentage,              // positive
        netAmountToCredit: netToCredit,            // ✅ withdraw + fee

        platform: formData.platform,
        notes: `Withdrawal from Ad Account: ${formData.accountName} to Credit Account: ${formData.credit_account_name}`,
      })

      const data = response.data
      if (data?.statusCode === 200) {
        toast.success("Withdraw request submitted")
        setTransferComplete(true)

        setTimeout(() => {
          onSuccess?.()
          onClose?.()
        }, 1500)
      } else {
        toast.error(data?.message || "Failed to submit request")
      }
    } catch (error) {
      console.error("Withdraw error:", error)
      const msg = error.response?.data?.message || "Failed to process withdrawal"
      setErrors((p) => ({ ...p, api: msg }))
      toast.error(msg)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-2xl transition-all border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">

                {/* Header */}
                <div className="relative border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-xl font-[400] text-neutral-900 dark:text-white">
                      Withdraw Request
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : (
                    <>
                      {/* cards */}
                      <div className="flex flex-col md:flex-row gap-4 mb-6 relative">
                        {/* From: Ad Account */}
                        <div className="flex-1 relative rounded-xl border border-neutral-200 dark:border-neutral-900 bg-white dark:bg-neutral-900/30 shadow-sm overflow-hidden">
                          <div className="bg-neutral-100 dark:bg-neutral-700 px-4 py-2 flex items-center gap-2">
                            <Wallet size={16} className="text-neutral-500 dark:text-neutral-400" />
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                              Ad Account
                            </span>
                          </div>
                          <div className="p-4">
                            <div className="font-medium text-sm text-neutral-800 dark:text-white  mb-3">
                              {formData.accountName || "Ad Account"}
                              {formData.platform && (
                                <span className="ml-1 absolute flex justify-end items-center gap-1 bottom-2 right-2 text-xs text-neutral-500 dark:text-neutral-400">
                                  {getPlatformIcon(formData.platform)} ({getPlatformDisplayName(formData.platform)})
                                </span>
                              )}
                            </div>

                          </div>
                        </div>

                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex">
                          <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-700 shadow-md flex items-center justify-center">
                            <ArrowRight size={18} className="text-neutral-600 dark:text-neutral-300" />
                          </div>
                        </div>

                        <div className="flex justify-center md:hidden -my-2">
                          <div className="w-8 h-8 rounded-full bg-neutral-100 dark:bg-neutral-700 shadow-md flex items-center justify-center">
                            <ArrowRight size={16} className="text-neutral-600 dark:text-neutral-300 rotate-90" />
                          </div>
                        </div>

                        {/* To: Credit Account */}
                        <div className="flex-1 rounded-xl border border-neutral-200 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/30 shadow-sm overflow-hidden">
                          <div className="bg-neutral-100 dark:bg-neutral-700 px-4 py-2 flex items-center gap-2">
                            <CreditCard size={16} className="text-neutral-500 dark:text-neutral-400" />
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                              Credit Account
                            </span>
                          </div>
                          <div className="p-4">
                            <div className="font-medium text-sm text-neutral-800 dark:text-white truncate mb-3">
                              {formData.credit_account_name || "Credit Account"}
                            </div>
                            <div className="text-xs text-neutral-500 dark:text-neutral-400">Current Balance</div>
                            <div className="text-lg font-[600] text-neutral-900 dark:text-white">
                              ${formatCurrency(Number(formData.credit_account_balance) || 0)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Fee info */}
                      {feePercentage > 0 && (
                        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30 flex items-start gap-2">
                          <Info className="text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" size={16} />
                          <p className="text-xs text-blue-800 dark:text-blue-300">
                            A {feePercentage}% fee will be added for {getPlatformDisplayName(formData.platform)} withdrawals.
                          </p>
                        </div>
                      )}

                      {/* Success */}
                      {transferComplete ? (
                        <div className="flex flex-col items-center justify-center py-6">
                          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <h3 className="text-lg text-center font-medium text-neutral-900 dark:text-white">
                            Withdraw Request Submitted Successfully
                          </h3>
                        </div>
                      ) : (
                        <>
                          {/* Amount */}
                          <div className="mb-6">
                            <InputCustom
                              label="Withdrawal Amount"
                              placeholder="Enter Withdrawal Amount"
                              type="number"
                              value={transferAmount}
                              onChange={(value) => handleAmountChange(value)}
                              icon={<DollarSign size={18} className="text-neutral-500 dark:text-neutral-400" />}
                              error={errors.transferAmount}
                            />
                            {errors.transferAmount && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.transferAmount}
                              </p>
                            )}
                          </div>

                          {/* Breakdown */}
                          <div className="mb-6 space-y-3 p-4 rounded-xl bg-neutral-50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-800">
                            <div className="flex justify-between">
                              <span className="text-sm text-neutral-600 dark:text-neutral-400">Withdraw Amount</span>
                              <span className="text-sm text-neutral-800 dark:text-neutral-200">
                                ${formatCurrency(Number.parseFloat(transferAmount) || 0)}
                              </span>
                            </div>

                            {feePercentage > 0 && (
                              <div className="flex justify-between">
                                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                  Fee ({feePercentage}%)
                                </span>
                                <span className="text-sm text-neutral-800 dark:text-neutral-200">
                                  ${formatCurrency(feeAmount)}
                                </span>
                              </div>
                            )}

                            <div className="pt-2 border-t border-neutral-200 dark:border-neutral-800 flex justify-between">
                              <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                                Net Amount to Credit Account
                              </span>
                              <span className="text-sm font-[600] text-green-600 dark:text-green-400">
                                ${formatCurrency(netToCredit)}
                              </span>
                            </div>

                            <div className="pt-2 border-t border-neutral-200 dark:border-neutral-800 flex justify-between">
                              <span className="text-xs text-neutral-500 dark:text-neutral-400">Currency</span>
                              <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                                {formData.currency || formData.credit_account_currency || "USD"}
                              </span>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="w-full h-11 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors flex items-center justify-center text-sm font-[400] disabled:opacity-70 disabled:cursor-not-allowed"
                            onClick={handleWithdrawBalance}
                            disabled={isProcessing || !transferAmount || Number.parseFloat(transferAmount) <= 0}
                          >
                            {isProcessing ? (
                              <>
                                <RefreshCw size={18} className="animate-spin mr-2" />
                                Processing...
                              </>
                            ) : (
                              "Submit Request"
                            )}
                          </button>
                        </>
                      )}

                      {errors.api && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-xl flex items-start gap-2">
                          <AlertCircle className="text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" size={16} />
                          <span className="text-xs text-red-800 dark:text-red-300">{errors.api}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default WithdrawBalanceModal
